import { useState } from "react";
import { X, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

/**
 * TagInput — chip / pill multi-value input.
 *
 * Props:
 *   value:      string[]
 *   onChange:   (next: string[]) => void
 *   placeholder?: string
 *   chipClassName?: string   – tailwind classes applied to each chip
 *   suggestions?: string[]   – optional auto-suggest pool (case-insensitive)
 */
export default function TagInput({
  value = [],
  onChange,
  placeholder = "Type and press Enter…",
  chipClassName = "bg-primary/10 text-primary border-primary/20",
  suggestions = [],
}) {
  const [draft, setDraft] = useState("");
  const safe = Array.isArray(value) ? value : [];

  const add = (raw) => {
    const v = (raw || "").trim();
    if (!v) return;
    if (safe.some((t) => t.toLowerCase() === v.toLowerCase())) return;
    onChange([...safe, v]);
    setDraft("");
  };
  const remove = (idx) => onChange(safe.filter((_, i) => i !== idx));

  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(draft);
    } else if (e.key === "Backspace" && !draft && safe.length) {
      onChange(safe.slice(0, -1));
    }
  };

  const filteredSuggestions = draft
    ? suggestions
        .filter(
          (s) =>
            s.toLowerCase().includes(draft.toLowerCase()) &&
            !safe.some((t) => t.toLowerCase() === s.toLowerCase()),
        )
        .slice(0, 6)
    : [];

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {safe.map((t, i) => (
          <Badge
            key={`${t}-${i}`}
            className={`gap-1 capitalize border ${chipClassName}`}
            data-testid={`tag-chip-${i}`}
          >
            {t}
            <button
              type="button"
              onClick={() => remove(i)}
              className="ml-0.5 -mr-1 rounded-full hover:bg-black/20 p-0.5"
              aria-label={`Remove ${t}`}
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="relative flex gap-1.5">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={() => add(draft)}
          className="px-2 rounded-md border border-input bg-transparent hover:bg-muted text-xs flex items-center gap-1"
          aria-label="Add tag"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
        {filteredSuggestions.length > 0 && (
          <div className="absolute top-full left-0 right-12 mt-1 z-50 bg-popover border border-border rounded-md shadow-lg overflow-hidden">
            {filteredSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => add(s)}
                className="w-full text-left px-3 py-1.5 text-sm capitalize hover:bg-muted"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
